// Inloggningen i kontot (ADR 0062 och 0083). Token säger vem användaren är,
// och GET /v1/admin/me ger namn, roll och byrå (ADR 0029). I AWS loggar man
// in med Cognito, och access-token förnyas med refresh-token när den snart
// går ut. Inloggningen gäller i alla flikar (ADR 0109). Lokalt skapar Vites
// server en token för exempelbyrån.
import { createCognito, type AuthConfig, type Cognito, type Tokens } from '@leadledger/cognito';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiFel, createApi, data, type Api } from '../api/client.js';
import type { components } from '../api/schema.js';
import { hamtaLokalToken, type LokalRoll } from './lokal.js';
import {
  lasFornyelse,
  lasToken,
  sparaFornyelse,
  sparaToken,
  TOKEN_NYCKEL,
  webblasarensEnheter,
} from './token-store.js';

export type Me = components['schemas']['Me'];

export type Inloggning =
  | { status: 'utloggad' }
  | { status: 'laddar' }
  | { status: 'inloggad'; me: Me }
  /** Token gäller, men användaren hör inte till någon byrå (403). */
  | { status: 'ingen-byra' }
  /** API:t svarar inte, eller svarar med ett fel. */
  | { status: 'fel'; forsokIgen: () => void };

/** Hur man loggar in: lokalt, med Cognito, eller okänt medan API:t svarar. */
export type Satt =
  | { typ: 'laddar' }
  | { typ: 'fel'; forsokIgen: () => void }
  | { typ: 'lokal' }
  | { typ: 'cognito'; cognito: Cognito };

interface AuthKontext {
  inloggning: Inloggning;
  satt: Satt;
  api: Api;
  loggaInLokalt: (roll: LokalRoll) => Promise<void>;
  /** Token från Cognito, när alla steg i inloggningen är klara. */
  loggaInMed: (tokens: Tokens) => void;
  loggaUt: () => void;
}

const Kontext = createContext<AuthKontext | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** För testerna. */
  fetch?: typeof fetch;
  baseUrl?: string;
  /** För testerna: klockan i millisekunder. */
  nu?: () => number;
}

/** Webbläsarens namn för enheten i Cognito, till exempel Chrome på Windows. */
function webblasare(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const namn = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'webbläsare';
  const system = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return system ? `${namn} på ${system}` : namn;
}

/** Access-token förnyas när den har mindre än en minut kvar. */
const MARGINAL_MS = 60_000;

export function AuthProvider({ children, fetch: f, baseUrl, nu = Date.now }: AuthProviderProps) {
  const [token, setToken] = useState(lasToken);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const cognitoRef = useRef<Cognito | null>(null);
  const fornyar = useRef<Promise<string | null> | null>(null);
  const queryClient = useQueryClient();

  const loggaUt = useCallback(() => {
    const fornyelse = lasFornyelse();
    if (fornyelse && cognitoRef.current) {
      // Refresh-token görs ogiltig. Går det inte är man ändå utloggad här.
      void cognitoRef.current.loggaUt(fornyelse.refreshToken).catch(() => undefined);
    }
    sparaToken(null);
    tokenRef.current = null;
    setToken(null);
    queryClient.clear();
  }, [queryClient]);

  // Loggar en annan flik in eller ut gäller det också här (ADR 0109).
  useEffect(() => {
    const lyssna = (e: StorageEvent) => {
      if (e.key !== TOKEN_NYCKEL && e.key !== null) return;
      const ny = lasToken();
      if (ny === tokenRef.current) return;
      tokenRef.current = ny;
      setToken(ny);
      if (!ny) queryClient.clear();
    };
    window.addEventListener('storage', lyssna);
    return () => window.removeEventListener('storage', lyssna);
  }, [queryClient]);

  const sattIn = useCallback((ny: string) => {
    sparaToken(ny);
    tokenRef.current = ny;
    setToken(ny);
  }, []);

  const aktuellToken = useCallback(async (): Promise<string | null> => {
    const fornyelse = lasFornyelse();
    const cognito = cognitoRef.current;
    // En annan flik kan ha förnyat token, så den sparade är den senaste.
    if (!fornyelse || !cognito || fornyelse.expiresAt - nu() > MARGINAL_MS) {
      return lasToken() ?? tokenRef.current;
    }
    fornyar.current ??= cognito
      .fornya(fornyelse.refreshToken, fornyelse.deviceKey)
      .then((t) => {
        sparaFornyelse(undefined, t.expiresAt);
        sattIn(t.accessToken);
        return t.accessToken;
      })
      .catch(() => {
        loggaUt();
        return null;
      })
      .finally(() => {
        fornyar.current = null;
      });
    return fornyar.current;
  }, [loggaUt, nu, sattIn]);

  const api = useMemo(
    () =>
      createApi({
        token: aktuellToken,
        onUtloggad: loggaUt,
        ...(f ? { fetch: f } : {}),
        ...(baseUrl !== undefined ? { baseUrl } : {}),
      }),
    [f, baseUrl, loggaUt, aktuellToken],
  );

  const konfig = useQuery({
    queryKey: ['auth-config'],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => data(await api.GET('/v1/public/auth-config')) as AuthConfig,
  });

  const cognito = useMemo(() => {
    if (konfig.data?.provider !== 'cognito') return null;
    const { region, webClientId } = konfig.data;
    return createCognito({
      region,
      clientId: webClientId,
      // Webbläsaren slipper koden i 30 dagar efter en inloggning med kod (ADR 0108).
      enheter: webblasarensEnheter,
      enhetNamn: `LeadLedger-kontot, ${webblasare()}`,
      ...(f ? { fetch: f } : {}),
    });
  }, [konfig.data, f]);
  cognitoRef.current = cognito;

  const { data: konfigData, error: konfigFel, refetch: hamtaKonfig } = konfig;
  const satt = useMemo((): Satt => {
    if (cognito) return { typ: 'cognito', cognito };
    if (konfigData) return { typ: 'lokal' };
    if (konfigFel) return { typ: 'fel', forsokIgen: () => void hamtaKonfig() };
    return { typ: 'laddar' };
  }, [cognito, konfigData, konfigFel, hamtaKonfig]);

  const me = useQuery({
    queryKey: ['me', token],
    // Först när det är känt hur man loggar in, så att en gammal token hinner förnyas.
    enabled: token !== null && satt.typ !== 'laddar',
    retry: false,
    queryFn: async () => data(await api.GET('/v1/admin/me')),
  });

  const loggaInLokalt = useCallback(
    async (roll: LokalRoll) => {
      sattIn(await hamtaLokalToken(roll, f));
    },
    [f, sattIn],
  );

  const loggaInMed = useCallback(
    (tokens: Tokens) => {
      sparaFornyelse(tokens.refreshToken, tokens.expiresAt, tokens.deviceKey);
      sattIn(tokens.accessToken);
    },
    [sattIn],
  );

  const inloggning = useMemo((): Inloggning => {
    if (token === null) return { status: 'utloggad' };
    if (satt.typ === 'laddar') return { status: 'laddar' };
    if (me.data) return { status: 'inloggad', me: me.data };
    if (me.error) {
      if (me.error instanceof ApiFel && me.error.status === 403) return { status: 'ingen-byra' };
      if (me.error instanceof ApiFel && me.error.status === 401) return { status: 'utloggad' };
      return { status: 'fel', forsokIgen: () => void me.refetch() };
    }
    return { status: 'laddar' };
  }, [token, me, satt.typ]);

  const varde = useMemo(
    () => ({ inloggning, satt, api, loggaInLokalt, loggaInMed, loggaUt }),
    [inloggning, satt, api, loggaInLokalt, loggaInMed, loggaUt],
  );
  return <Kontext.Provider value={varde}>{children}</Kontext.Provider>;
}

export function useAuth(): AuthKontext {
  const k = useContext(Kontext);
  if (!k) throw new Error('useAuth används utanför AuthProvider');
  return k;
}

/** Den inloggade användaren. Används bara inuti sidor som kräver inloggning. */
export function useMe(): Me {
  const { inloggning } = useAuth();
  if (inloggning.status !== 'inloggad') throw new Error('Inte inloggad');
  return inloggning.me;
}
